import {
  ConstructionType,
  ConstructionTypeDisplayName,
} from "pmt-types/src/enums/ConstructionType.ts";
import {
  ProgramType,
  ProgramTypeDisplayName,
} from "pmt-types/src/enums/ProgramType.ts";
import {
  ProjectStatus,
  ProjectStatusDisplayName,
} from "pmt-types/src/enums/ProjectStatus.ts";
import {
  Signature,
  SignatureDisplayName,
  SignatureEnum,
} from "pmt-types/src/enums/Signature.ts";
import { UnitedStates } from "pmt-types/src/enums/UnitedStates.ts";
import { Project, ProjectSchema } from "pmt-types/src/models/Project.ts";
import { useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import EnumSelect from "../../components/form/enumSelect.tsx";
import FormInput from "../../components/form/formInput.tsx";
import NumberInput from "../../components/form/numberInput.tsx";
import PmtForm from "../../components/form/pmtForm.tsx";
import SearchableMultiSelect from "../../components/form/searchableMultiSelect.tsx";
import FormRoot from "../../components/form/style/formRoot.tsx";
import FormSection from "../../components/form/style/formSection.tsx";
import TabbedSelect from "../../components/form/tabbedSelect.tsx";
import TextArea from "../../components/form/textArea.tsx";
import LoadingSpinner from "../../components/loadingSpinner.tsx";
import NotFoundPage from "../../global/notFoundPage.tsx";
import { useMutationSearchContractors } from "../../hooks/mutations/useMutationSearchContractors.ts";
import { useMutationSearchFirstNamedInsureds } from "../../hooks/mutations/useMutationSearchFirstNamedInsureds.ts";
import { useMutationSearchProjectContacts } from "../../hooks/mutations/useMutationSearchProjectContacts.ts";
import { useMutationUpsertProject } from "../../hooks/mutations/useMutationUpsertProject.ts";
import { QueryKeys } from "../../hooks/queries/queryKeys.ts";
import { useQueryGetProjectById } from "../../hooks/queries/useQueryGetProjectById.ts";
import { useQueryGetSourceById } from "../../hooks/queries/useQueryGetSourceById.ts";
import useQueryParams from "../../hooks/useQueryParams.ts";
import enumToLabelValue from "../../utils/transforms/enumToLabelValue.ts";
import { transformSelectInput } from "../../utils/transforms/transformSelectInput.ts";

export default function EditProject() {
  const navigate = useNavigate();
  const { sourceId, projectId } = useParams();
  const queryParams = useQueryParams();
  const queryClient = useQueryClient();

  const { data: source, isFetching: isFetchingSource } =
    useQueryGetSourceById(sourceId);
  const { data: project, isFetching: isFetchingProject } =
    useQueryGetProjectById(sourceId, projectId);

  const { mutateAsync: searchContractors } = useMutationSearchContractors();
  const { mutateAsync: searchFirstNamedInsureds } =
    useMutationSearchFirstNamedInsureds();
  const { mutateAsync: searchProjectContacts } =
    useMutationSearchProjectContacts();
  const { mutate: upsertProject, isLoading: isUpsertingProject } =
    useMutationUpsertProject();

  const isNew = projectId === "new";

  if (isFetchingSource || isFetchingProject) {
    return <LoadingSpinner />;
  }

  if (source == null || (!isNew && project == null)) {
    return <NotFoundPage />;
  }

  const defaultFormValues = {
    source: {
      id: sourceId,
      name: source.name,
    },
    firstNamedInsureds: source?.firstNamedInsureds,
    descriptionOfOperations: `The Certificate Holder is an Insured under the Owner Controlled Insurance Program as specified in the contract documents.`,
    projectContacts: ["Joanna Burleson"],
  };

  const navigateAfterSave = (project: Project) => {
    if (sourceId != null && queryParams.get("return") === "source") {
      navigate(`/sources/${sourceId}/details`);
    } else if (queryParams.get("return") === "search") {
      navigate(`/projects`);
    } else {
      navigate(`/sources/${project.source.id}/projects/${project.id}/details`);
    }
  };

  return (
    <PmtForm<Project>
      schema={ProjectSchema}
      pageTitle={`${source.name} / ${
        isNew ? "New Project" : project?.name ?? "Edit Project"
      }`}
      defaultValues={{
        ...defaultFormValues,
        ...(project ?? {}),
        id: projectId ?? "new",
      }}
      submitMutation={upsertProject}
      onSave={async (project) => {
        await queryClient.invalidateQueries({
          queryKey: [QueryKeys.GetProject, project.id],
        });
        await queryClient.invalidateQueries({
          queryKey: [QueryKeys.GetSourceProjects, project.id],
        });
        navigateAfterSave(project);
      }}
      onCancel={() => navigate(-1)}
      isSubmitting={isUpsertingProject}
      renderForm={() => (
        <FormRoot>
          <FormSection>
            <FormInput name="name" label="Project Name" />
            <TabbedSelect
              name="status"
              label="Status"
              tabs={enumToLabelValue(
                ProjectStatus.enum,
                ProjectStatusDisplayName,
              )}
            />
            <FormInput name="startDate" label="Start Date" type="date" />
            <FormInput name="endDate" label="End Date" type="date" />
          </FormSection>
          <FormSection label={"Construction Details"}>
            <TabbedSelect
              name="programType"
              label="Program Type"
              tabs={enumToLabelValue(ProgramType.enum, ProgramTypeDisplayName)}
            />
            <NumberInput
              name="constructionValue"
              label="Construction Value"
              prefix="$"
              min={0}
              step={10000}
            />
            <NumberInput
              name="estimatedSalesValue"
              label="Estimated Sales Value"
              prefix="$"
              min={0}
              step={10000}
            />
            <TabbedSelect
              name="constructionType"
              label="Construction Type"
              tabs={enumToLabelValue(
                ConstructionType.enum,
                ConstructionTypeDisplayName,
              )}
            />
            <SearchableMultiSelect
              name="contractors"
              label="Contractor"
              transform={transformSelectInput}
              queryFn={searchContractors}
            />
            <TextArea
              name="descriptionOfOperations"
              label="Description of Operations"
            />
          </FormSection>
          <FormSection label={"Address"}>
            <FormInput name="address.street" label="Street" />
            <FormInput name="address.city" label="City" />
            <FormInput name="address.zipCode" label="Zip Code" />
            <EnumSelect
              name="address.state"
              label="State"
              data={UnitedStates.options}
            />
          </FormSection>
          <FormSection label={"Policy Details"}>
            <SearchableMultiSelect
              name="firstNamedInsureds"
              label="First Named Insured"
              transform={transformSelectInput}
              queryFn={searchFirstNamedInsureds}
            />
            <EnumSelect
              name="signature"
              label="Signature"
              data={Signature.options}
              formatOption={(option) =>
                SignatureDisplayName[option as SignatureEnum]
              }
            />
            <SearchableMultiSelect
              name="projectContacts"
              label="Project Contact"
              transform={transformSelectInput}
              queryFn={searchProjectContacts}
            />
          </FormSection>
        </FormRoot>
      )}
    />
  );
}
